export type AppRole = "user" | "admin" | "systemadmin";

export type BackendRole = "STUDENT" | "TRAINER" | "TEACHER" | "MANAGER" | "SYSTEM_ADMIN";

/**
 * Map backend role → app-level role (frontend routing level)
 *   STUDENT / TRAINER           → "user"
 *   TEACHER / MANAGER           → "admin"
 *   SYSTEM_ADMIN                → "systemadmin"
 */
export function toAppRole(backendRole: string): AppRole {
  switch (backendRole) {
    case "TEACHER":
    case "MANAGER":
      return "admin";
    case "SYSTEM_ADMIN":
      return "systemadmin";
    default:
      return "user";
  }
}

/**
 * Decode JWT payload without verifying signature (client-side only).
 * Signature verification happens on the backend per request.
 */
export function decodeJwt(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return null;
  }
}

/** Get the app role from the stored access token, or null if not logged in. */
export function getCurrentAppRole(): AppRole | null {
  const token = sessionStorage.getItem("access_token");
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload?.role) return null;
  return toAppRole(payload.role as string);
}

/** Get raw backend role from access token (e.g. MANAGER, TEACHER). */
export function getCurrentBackendRole(): BackendRole | null {
  const token = sessionStorage.getItem("access_token");
  if (!token) return null;
  const payload = decodeJwt(token);
  if (!payload?.role || typeof payload.role !== "string") return null;
  return payload.role as BackendRole;
}

/** Get the home path for a given app role. */
export function roleHomePath(role: AppRole): string {
  switch (role) {
    case "admin":
      return "/admin";
    case "systemadmin":
      return "/systemadmin";
    default:
      return "/user";
  }
}
